import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { FaTimes, FaSave, FaCheck, FaChevronDown, FaChevronUp } from "react-icons/fa";
import { generateUSAFormationPDF } from "./pdfGenerator";
import companyLogo from "../../Assets/BooksCompl.png";
import { toast } from "../../components/ui/Toast";
import { buildLegacyFormQuestions, formatTemplate as formatUsaTemplate, getActiveFormSections } from "../../portal";

const QuestionForm = ({
  onClose, onSubmit, initialAnswers, initialComments, isSubmitted, companyType = "LLC",
  forms = null,
  sections = null,
  checklistModal = {},
  toasts = {},
  pdfMeta = {},
}) => {
  const [answers, setAnswers] = useState(initialAnswers || {});
  const [comments, setComments] = useState(initialComments || {});
  const [expanded, setExpanded] = useState({});
  const [selectedBusinessType, setSelectedBusinessType] = useState(companyType);
  const [showPdfPreview, setShowPdfPreview] = useState(false);
  const [pdfBlob, setPdfBlob] = useState(null);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const modalRoot = typeof document !== 'undefined' ? document.body : null;

  const activeSections = forms
    ? getActiveFormSections(forms, selectedBusinessType)
    : (sections || []);
  const legacyFromCms = activeSections.length ? buildLegacyFormQuestions(activeSections) : null;

  const fallbackCCorpQuestions = {
    "Basic Information": [
      "What Type of Business Would You Like To Start? (LLC or C-Corp)",
      "In which U.S. state do you want to incorporate the company?",
      "What Would You Like Your Business Name To Be?",
      "Business Purpose (Be Specific)",
      "Physical Address You Want On Record With The State: (Our Registered Agent Address or Your Mailing Address)",
      "Mailing Address You Want On Record With The State: (Our Registered Agent Address or Your Mailing Address)"
    ],
    "Management - President": [
      "First Name",
      "Last Name",
      "Address To Record With The State: (Our Registered Agent Address or Your Mailing Address)"
    ],
    "Management - Secretary": [
      "First Name",
      "Last Name",
      "Address To Record With The State: (Our Registered Agent Address or Your Mailing Address)"
    ],
    "Management - Treasurer": [
      "First Name",
      "Last Name",
      "Address To Record With The State: (Our Registered Agent Address or Your Mailing Address)",
    ],
    "Shares & Other Informations": [
      "Number of Authorized Shares",
      "Par Value",
      "All officers are directors"
    ],
    "Responsible Party": [
      "First Name",
      "Last Name",
      "Email",
      "Phone Number (USA)",
      "India Address - Line1",
      "India Address - Line2",
      "City",
      "State",
      "Zipcode",
      "Country",
      "You have, or you expect to have, any employees who will receive Forms W-2 in the next 12 months. (Yes or No)",
      "Fiscal Year-End Month"
    ]
  };

  const fallbackLlcQuestions = {
    "Basic Information": [
      "What Type of Business Would You Like To Start? (LLC or C-Corp)",
      "In which U.S. state do you want to incorporate the company?",
      "What Would You Like Your Business Name To Be?",
      "Business Purpose (Be Specific)",
      "Physical Address You Want On Record With The State: (Our Registered Agent Address or Your Mailing Address)",
      "Mailing Address You Want On Record With The State: (Our Registered Agent Address or Your Mailing Address)"
    ],
    "Partners Information - Partner 1": [
      "First Name",
      "Last Name",
      "Percentage Holding"
    ],
    "Partners Information - Partner 2": [
      "First Name",
      "Last Name",
      "Percentage Holding"
    ],
    "Partners Information - Partner 3": [
      "First Name",
      "Last Name",
      "Percentage Holding"
    ],
    "Responsible Party": [
      "First Name",
      "Last Name",
      "Email",
      "Phone Number (USA)",
      "India Address - Line1",
      "India Address - Line2",
      "City",
      "State",
      "Zipcode",
      "Country",
      "You have, or you expect to have, any employees who will receive Forms W-2 in the next 12 months. (Yes or No)"
    ]
  };

  const questionMeta = legacyFromCms?.questionMeta || {};

  const isMultiChoiceQuestion = (q, questionKey) => {
    const meta = questionMeta[questionKey];
    if (meta?.inputType && meta.inputType !== 'text') return true;
    return q.includes("(LLC or C-Corp)") ||
      q.includes("(Yes or No)") ||
      q.includes("Our Registered Agent Address or Your Mailing Address") ||
      q.includes("employees who will receive Forms W-2");
  };

  const formQuestions = legacyFromCms?.formQuestions
    || (selectedBusinessType === "C-Corp" ? fallbackCCorpQuestions : fallbackLlcQuestions);

  const totalQuestions = Object.values(formQuestions).reduce(
    (total, section) => total + section.length,
    0
  );

  const handleAnswer = (index, value) => {
    if (!isSubmitted) {
      setAnswers((prev) => ({ ...prev, [index]: value }));
      setIsDirty(true);
    }
  };

  const handleComment = (index, value) => {
    if (!isSubmitted) {
      setComments((prev) => ({ ...prev, [index]: value }));

      // If it's a direct text question, ensure answer state is set to 'comment'
      // so it gets counted in progress and PDF generation
      if (answers[index] === undefined || answers[index] === null) {
        setAnswers((prev) => ({ ...prev, [index]: "comment" }));
      }
      setIsDirty(true);
    }
  };

  const toggleQuestion = (question) => {
    if (!isSubmitted) {
      setExpanded((prev) => ({ ...prev, [question]: !prev[question] }));
    }
  };

  const calculateProgress = () => {
    return Object.keys(formQuestions).reduce((total, section) => {
      return total + calculateSectionProgress(section);
    }, 0);
  };

  const calculateSectionProgress = (section) => {
    return formQuestions[section].filter((_, index) => {
      const questionKey = `${section}-${index}`;
      const answer = answers[questionKey];

      // Not answered at all
      if (answer === undefined || answer === null || answer === "") return false;

      // If it's a comment-based answer, check if there's actual text
      if (answer === "comment" || answer === "Your Mailing Address") {
        return comments[questionKey] && comments[questionKey].trim() !== "";
      }

      // Selection-based answer
      return true;
    }).length;
  };

  const handleSave = () => {
    if (!isSubmitted) {
      const currentProgress = calculateProgress();
      onSubmit({
        answers: {
          ...answers,
          'businessType': selectedBusinessType // Ensure business type is always included
        },
        comments,
        progress: currentProgress
      }, false);
      setIsDirty(false);
      toast.success(formatUsaTemplate(
        toasts.saveProgressSuccess || `Saved progress: {current}/{total} questions answered`,
        { current: currentProgress, total: totalQuestions }
      ));
    }
  };

  const handleClose = () => {
    if (isDirty && !isSubmitted) {
      setShowUnsavedModal(true);
    } else {
      onClose();
    }
  };

  const handleConfirm = async () => {
    const currentProgress = calculateProgress();
    if (currentProgress < totalQuestions) {
      toast.warning(formatUsaTemplate(
        toasts.submitIncomplete || `Please answer all questions before submitting. ({current}/{total} answered)`,
        { current: currentProgress, total: totalQuestions }
      ));
      return;
    }

    if (!isSubmitted) {
      try {
        // Save current progress before preview
        await onSubmit({
          answers: {
            ...answers,
            'businessType': selectedBusinessType
          },
          comments,
          progress: currentProgress
        }, false);

        const blob = generateUSAFormationPDF(
          formQuestions,
          answers,
          comments,
          selectedBusinessType,
          companyLogo
        );

        const url = URL.createObjectURL(blob);
        setPdfBlob(blob);
        setPdfPreviewUrl(url);
        setShowPdfPreview(true);
      } catch (error) {
        console.error("Error generating PDF preview:", error);
        toast.error(formatUsaTemplate(toasts.pdfPreviewFailed || 'Failed to generate PDF preview: {message}', { message: error.message }));
      }
    }
  };

  const handleFinalSubmit = async () => {
    if (isSubmitting) return;

    try {
      setIsSubmitting(true);

      const pdfFormData = new FormData();
      const pdfName = `USA Company Formation Checklist - ${selectedBusinessType}.pdf`;
      pdfFormData.append('pdf', pdfBlob, pdfName);

      const pdfToken = localStorage.getItem("token");
      const pdfResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/upload-pdf`, {
        method: "POST",
        body: pdfFormData,
        headers: {
          Authorization: `Bearer ${pdfToken}`,
        },
      });

      if (!pdfResponse.ok) {
        throw new Error("Failed to upload PDF");
      }

      const pdfResult = await pdfResponse.json();

      onSubmit({
        answers: {
          ...answers,
          'businessType': selectedBusinessType
        },
        comments,
        pdfUrl: pdfResult.pdfUrl,
        progress: calculateProgress()
      }, true);

      // Clean up the URL
      if (pdfPreviewUrl) {
        URL.revokeObjectURL(pdfPreviewUrl);
      }

      setShowPdfPreview(false);
      toast.success(toasts.formSubmitted || "Form submitted successfully!");
    } catch (error) {
      console.error("Error in final submission:", error);
      toast.error(formatUsaTemplate(toasts.formSubmitFailed || 'Failed to submit form: {message}', { message: error.message }));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClosePreview = () => {
    if (pdfPreviewUrl) {
      URL.revokeObjectURL(pdfPreviewUrl);
      setPdfPreviewUrl(null);
    }
    setShowPdfPreview(false);
  };

  const renderQuestionInput = (questionTitle, subIndex, subQuestion) => {
    const questionKey = `${questionTitle}-${subIndex}`;
    const meta = questionMeta[questionKey];
    const inputType = meta?.inputType;

    if (inputType === 'businessType' || subQuestion.includes("What Type of Business Would You Like To Start?")) {
      const options = meta?.choices?.length ? meta.choices : ["LLC", "C-Corp"];
      return (
        <div className="flex flex-wrap gap-2 mt-2">
          {options.map((option) => (
            <button
              key={option}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${answers[questionKey] === option
                  ? 'bg-primary-500 text-white shadow-md'
                  : 'bg-gray-100 dark:bg-dark-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-dark-600'
                } ${isSubmitted ? 'opacity-50 cursor-not-allowed' : ''}`}
              onClick={(e) => {
                e.stopPropagation();
                handleAnswer(questionKey, option);
                setSelectedBusinessType(option);
              }}
              disabled={isSubmitted}
            >
              {option}
            </button>
          ))}
        </div>
      );
    }

    if (inputType === 'yesNo' || subQuestion.includes("Yes or No") || subQuestion.includes("employees who will receive Forms W-2")) {
      const options = meta?.choices?.length ? meta.choices : ["Yes", "No"];
      return (
        <div className="flex flex-wrap gap-2 mt-2">
          {options.map((option) => (
            <button
              key={option}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${answers[questionKey] === option
                  ? 'bg-primary-500 text-white shadow-md'
                  : 'bg-gray-100 dark:bg-dark-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-dark-600'
                } ${isSubmitted ? 'opacity-50 cursor-not-allowed' : ''}`}
              onClick={(e) => {
                e.stopPropagation();
                handleAnswer(questionKey, option);
              }}
              disabled={isSubmitted}
            >
              {option}
            </button>
          ))}
        </div>
      );
    }

    if (inputType === 'addressChoice' || subQuestion.includes("Our Registered Agent Address or Your Mailing Address")) {
      const options = meta?.choices?.length ? meta.choices : ["Our Registered Agent Address", "Your Mailing Address"];
      return (
        <div className="flex flex-wrap gap-2 mt-2">
          {options.map((option) => (
            <button
              key={option}
              className={`px-3 py-2 rounded-lg text-xs font-medium transition-all ${answers[questionKey] === option
                  ? 'bg-primary-500 text-white shadow-md'
                  : 'bg-gray-100 dark:bg-dark-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-dark-600'
                } ${isSubmitted ? 'opacity-50 cursor-not-allowed' : ''}`}
              onClick={(e) => {
                e.stopPropagation();
                handleAnswer(questionKey, option);
              }}
              disabled={isSubmitted}
            >
              {option}
            </button>
          ))}
          <button
            className={`px-3 py-2 rounded-lg text-xs font-medium transition-all ${answers[questionKey] === "comment"
                ? 'bg-orange-500 text-white shadow-md'
                : 'bg-gray-100 dark:bg-dark-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-dark-600'
              } ${isSubmitted ? 'opacity-50 cursor-not-allowed' : ''}`}
            onClick={(e) => {
              e.stopPropagation();
              handleAnswer(questionKey, "comment");
            }}
            disabled={isSubmitted}
          >
            {checklistModal.customAddressLabel || 'CUSTOM'}
          </button>
        </div>
      );
    }

    // If not a multi-choice question, we return null because it will show
    // the textarea directly in the main loop
    if (!isMultiChoiceQuestion(subQuestion, questionKey)) {
      return null;
    }

    // Default comment input for multi-choice questions that allow custom response
    return (
      <div className="flex flex-wrap gap-2 mt-2">
        <button
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${answers[questionKey] === "comment"
              ? 'bg-orange-500 text-white shadow-md'
              : 'bg-gray-100 dark:bg-dark-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-dark-600'
            } ${isSubmitted ? 'opacity-50 cursor-not-allowed' : ''}`}
          onClick={(e) => {
            e.stopPropagation();
            handleAnswer(questionKey, "comment");
          }}
          disabled={isSubmitted}
        >
          {checklistModal.commentLabel || 'COMMENT'}
        </button>
      </div>
    );
  };

  useEffect(() => {
    if (!modalRoot) return;
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = overflow;
    };
  }, [modalRoot]);

  if (!modalRoot) {
    return null;
  }

  return createPortal(
    <div className="fixed inset-0 z-[1200] flex items-center justify-center bg-black/60 backdrop-blur-sm sm:p-4 overflow-y-auto">
      <div className="bg-white dark:bg-dark-800 w-full h-full sm:h-auto sm:max-h-[90vh] sm:max-w-4xl lg:max-w-6xl sm:rounded-2xl shadow-2xl flex flex-col animate-scale-in">
        {/* Header */}
        <div className="p-6 border-b border-gray-100 dark:border-dark-700">
          <div className="flex items-center justify-between mb-4">
            <h2 className="page-title">
              {checklistModal.title || 'USA Company Formation Checklist'}
            </h2>
            <div className="flex items-center gap-3">
              <span className="px-4 py-2 rounded-full bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400 font-semibold text-sm">
                {calculateProgress()}/{totalQuestions}
              </span>
              <button
                onClick={handleClose}
                className="p-2 rounded-xl bg-red-500 hover:bg-red-600 text-white transition-colors"
              >
                <FaTimes className="w-4 h-4" />
              </button>
            </div>
          </div>
          <div className="p-4 rounded-xl bg-primary-50 dark:bg-primary-900/20 border border-primary-100 dark:border-primary-800/30 text-sm text-primary-700 dark:text-primary-300">
            {checklistModal.introBanner || 'Please complete the checklist carefully. Our team will review your submission and guide you through the remaining steps.'}
          </div>
        </div>

        {/* Questions Container */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {Object.keys(formQuestions).map((questionTitle, index) => {
            const sectionLetter = String.fromCharCode(65 + index);
            const sectionProgress = calculateSectionProgress(questionTitle);
            const isExpanded = expanded[questionTitle];
            return (
              <div
                key={index}
                className={`rounded-xl border transition-all ${isExpanded
                    ? 'border-primary-300 dark:border-primary-700 bg-primary-50/50 dark:bg-primary-900/10'
                    : 'border-gray-200 dark:border-dark-700 bg-white dark:bg-dark-800'
                  }`}
              >
                <div
                  onClick={() => toggleQuestion(questionTitle)}
                  className="flex items-center justify-between p-4 cursor-pointer"
                >
                  <div className="flex items-center gap-3">
                    <span className="w-8 h-8 rounded-lg bg-primary-500 text-white flex items-center justify-center text-sm font-bold">
                      {sectionLetter}
                    </span>
                    <span className="font-semibold text-dark-800 dark:text-white">{questionTitle}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 dark:bg-dark-700 text-gray-600 dark:text-gray-400">
                      {sectionProgress}/{formQuestions[questionTitle].length}
                    </span>
                    {isExpanded ? (
                      <FaChevronUp className="w-4 h-4 text-gray-400" />
                    ) : (
                      <FaChevronDown className="w-4 h-4 text-gray-400" />
                    )}
                  </div>
                </div>

                {isExpanded && (
                  <div className="px-4 pb-4 space-y-4">
                    {formQuestions[questionTitle].map((subQuestion, subIndex) => {
                      const questionKey = `${questionTitle}-${subIndex}`;
                      const meta = questionMeta[questionKey];
                      return (
                      <div key={subIndex} className="p-4 rounded-xl bg-white dark:bg-dark-800 border border-gray-100 dark:border-dark-700">
                        <p className="text-sm text-dark-800 dark:text-gray-300 mb-2">
                          <span className="font-semibold text-primary-600 dark:text-primary-400 mr-2">{subIndex + 1}.</span>
                          {subQuestion}
                        </p>
                        {meta?.helpText && subIndex === 0 && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 italic mb-2">
                            * Note: {meta.helpText}
                          </p>
                        )}
                        {!meta?.helpText && subQuestion.includes("President") && subQuestion.includes("First Name") && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 italic mb-2">
                            * Note: The President is the operations manager, in charge of day to day operations.
                          </p>
                        )}
                        {subQuestion.includes("Secretary") && subQuestion.includes("First Name") && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 italic mb-2">
                            * Note: The Secretary documents corporate transactions like amendments, annual reports, and licenses.
                          </p>
                        )}
                        {subQuestion.includes("Treasurer") && subQuestion.includes("First Name") && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 italic mb-2">
                            * Note: The Treasurer is in charge of financial transactions.
                          </p>
                        )}
                        {renderQuestionInput(questionTitle, subIndex, subQuestion)}
                        {(!isMultiChoiceQuestion(subQuestion, questionKey) ||
                          answers[questionKey] === "comment" ||
                          answers[questionKey] === "Your Mailing Address") && (
                            <textarea
                              placeholder={checklistModal.textareaPlaceholder || "Add your response..."}
                              value={comments[questionKey] || ""}
                              onChange={(e) => handleComment(questionKey, e.target.value)}
                              className="w-full mt-2 p-2.5 rounded-xl bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600 text-dark-800 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500 resize-none"
                              rows={2}
                              disabled={isSubmitted}
                              onClick={(e) => e.stopPropagation()}
                            />
                          )}
                      </div>
                    );})}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Footer Actions */}
        <div className="p-4 sm:p-6 border-t border-gray-100 dark:border-dark-700 flex flex-row items-center justify-end gap-3 bg-white dark:bg-dark-800">
          {!isSubmitted && (
            <div className="flex flex-row items-center gap-3 w-full sm:w-auto overflow-x-auto no-scrollbar">
              <button
                onClick={handleSave}
                className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/20 hover:bg-primary-100 dark:hover:bg-primary-900/40 transition-all duration-200 whitespace-nowrap text-sm"
              >
                <FaSave className="w-4 h-4" />
                {checklistModal.saveProgressLabel || 'Save Progress'}
              </button>
              <button
                onClick={handleConfirm}
                className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-white bg-gradient-to-r from-green-500 to-green-600 shadow-lg shadow-green-500/30 hover:shadow-xl hover:shadow-green-500/40 transition-all duration-200 whitespace-nowrap text-sm"
              >
                <FaCheck className="w-4 h-4" />
                Submit
              </button>
            </div>
          )}

          {isSubmitted && (
            <div className="w-full sm:w-auto px-6 py-3 rounded-xl bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 font-semibold text-center whitespace-nowrap">
              {checklistModal.formSubmittedLabel || 'Form Submitted'}
            </div>
          )}
        </div>
      </div>

      {/* Unsaved Changes Confirmation Modal */}
      {showUnsavedModal && (
        <div className="fixed inset-0 z-[1400] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-2xl p-6 max-w-sm w-full animate-scale-in">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">{checklistModal.unsavedTitle || 'Unsaved Changes'}</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">{checklistModal.unsavedBody || 'You have unsaved changes. Are you sure you want to close without saving?'}</p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowUnsavedModal(false)}
                className="flex-1 px-4 py-2.5 rounded-xl font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 dark:bg-dark-700 dark:text-gray-400 dark:hover:bg-dark-600 transition-colors"
              >
                {checklistModal.stayLabel || 'Stay'}
              </button>
              <button
                onClick={() => { setShowUnsavedModal(false); onClose(); }}
                className="flex-1 px-4 py-2.5 rounded-xl font-semibold text-white bg-red-500 hover:bg-red-600 transition-colors"
              >
                {checklistModal.closeAnywayLabel || 'Close Anyway'}
              </button>
            </div>
          </div>
        </div>
      )}
      {showPdfPreview && (
        <div className="fixed inset-0 z-[1300] flex items-center justify-center bg-black/80 backdrop-blur-md p-0 sm:p-4 animate-fade-in">
          <div className="bg-white dark:bg-dark-800 w-full h-full sm:h-[95vh] sm:max-w-5xl sm:rounded-2xl shadow-2xl flex flex-col overflow-hidden">
            {/* Preview Header */}
            <div className="p-4 sm:p-6 border-b border-gray-100 dark:border-dark-700 flex items-center justify-between bg-white dark:bg-dark-800">
              <div>
                <h3 className="text-lg sm:section-title">{checklistModal.pdfPreviewTitle || 'Review Your Checklist'}</h3>
                <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">{checklistModal.pdfPreviewSubtitle || 'Please review the details below before final submission'}</p>
              </div>
              <button
                onClick={handleClosePreview}
                className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-dark-700 text-gray-500 dark:text-gray-400 transition-colors"
                disabled={isSubmitting}
              >
                <FaTimes className="w-5 h-5" />
              </button>
            </div>

            {/* PDF Viewer */}
            <div className="flex-1 bg-gray-100 dark:bg-dark-900 overflow-hidden relative">
              {pdfPreviewUrl && (
                <iframe
                  src={`${pdfPreviewUrl}#toolbar=0&navpanes=0&view=FitH`}
                  className="w-full h-full border-none"
                  title="PDF Preview"
                />
              )}
            </div>

            {/* Preview Footer */}
            <div className="p-4 sm:p-6 border-t border-gray-100 dark:border-dark-700 bg-white dark:bg-dark-800 flex flex-col sm:flex-row items-center gap-3">
              <button
                onClick={handleClosePreview}
                className="w-full sm:w-auto px-8 py-3 rounded-xl font-semibold text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-dark-700 hover:bg-gray-200 dark:hover:bg-dark-600 transition-all duration-200"
                disabled={isSubmitting}
              >
                Back to Edit
              </button>
              <button
                onClick={handleFinalSubmit}
                disabled={isSubmitting}
                className="w-full sm:flex-1 sm:max-w-md px-8 py-3 rounded-xl font-semibold text-white bg-gradient-to-r from-primary-600 to-primary-700 shadow-lg shadow-primary-500/30 hover:shadow-xl hover:shadow-primary-500/40 transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed sm:ml-auto"
              >
                {isSubmitting ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    Submitting...
                  </>
                ) : (
                  <>
                    <FaCheck className="w-4 h-4" />
                    Confirm & Submit
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>,
    modalRoot
  );
};

export default QuestionForm;